import { Injectable, inject } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { AppGlobalService } from 'src/app/services/app.global.service';
import * as re from './reference.enum';
import { GlobalConstants } from 'src/app/base/global.constants';
import referenceConfirmation from './reference-confirmation.json';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { UtilService } from 'src/app/services/util.service';
import { ReferenceTreeViewComponent } from './reference-tree-view.component';

export interface ModelReferencePayload {
  modelId: string;
  modelType: string;
  actionType: string;
  action: string;
  appId?: string; // optional at call sites; service will inject if absent
  memberApp?: boolean;
  sharedDB?: boolean;
  targetMemberApp?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ReferenceUtilService {
  private appGlobalService = inject(AppGlobalService);
  private translate = inject(TranslateService);
  private http = inject(HttpBaseService);
  private dialog = inject(DialogService);
  private util = inject(UtilService);

  confirmationMessageIds: Record<string, string> = {};
  confirmationMessageObj: Record<string, string> = {};
  private messageBundleLoaded = false;
  // Map internal names to display names for path construction
  // NOTE: List View, Detail View, Home Page, App Navigation removed since frontend shows page type as first element
  pageProperties: Record<string, string> = {
    Page: 'Page',
    'Title Bar': 'Title Bar',
    'Bread Crumb': 'Bread crumb',
    'Action bar': 'Action Bar',
    'Right Action bar': 'Right action bar',
    'Table Search': 'Table Search',
    'Quick Filters': 'Quick Filter',
    Table: 'Grid',
    'Workflow Action Bar': 'Workflow Action Bar',
    'Caption bar': 'Caption Bar',
    Form: 'Form',
    'Left Menu': 'Left Menu',
    'Left Area': 'Left Pane',
    'Right Area': 'Right Pane',
    Tabs: 'Navigation Tabs',
    'Page Title': 'Page Title',
    'Page Count': 'Page Count',
    'Description': 'Inline Text',
    'Filters and Search': 'Filters And Search',
    'Button Group': 'Button Group',
    Menu: 'Menu'
  };

  // Subject to emit refresh events
  private refreshSubject = new Subject<any>();
  refresh$ = this.refreshSubject.asObservable();

  constructor() {
    this.initializeMessages();
  }

  validateReferencesAndOpen(params: {
    node: { name: string; type: string; appId?: string };
    extras?: Record<string, unknown>;
    onError?: (err: unknown) => void;
    onClose?: () => void;
  }): void {
    const { node, extras, onError, onClose } = params;
    const body = node.appId ? { appId: node.appId } : {};
    this.http.post(ApiConstants.validateReferences, body).subscribe({
      next: (res: any) => {
        if (res?.missingReferences || res?.staleReferences) {
          const missingReferences = Array.isArray(res.missingReferences) ? res.missingReferences : [];
          const staleReferences = Array.isArray(res.staleReferences) ? res.staleReferences : [];
          if (missingReferences.length === 0 && staleReferences.length === 0) {
            if (onClose) {
              onClose();
            }
            return;
          }
          this.openReferenceTree(node, missingReferences, 'VALIDATE', {
            ...(extras || {}),
            validateMode: true,
            validateTabs: true,
            missingReferences,
            staleReferences
          })
            .then(() => {
              if (onClose) {
                onClose();
              }
            })
            .catch(() => {
              if (onClose) {
                onClose();
              }
            });
        } else if (res?.ERROR_CODE) {
          this.util.showMessage('error', this.translate.instant(res?.MESSAGE));
        } else {
          this.util.showMessage('success', this.translate.instant('ALL_REFERENCES_VALID'));
        }
      },
      error: (err) => {
        if (onError) {
          onError(err);
        }
        if (onClose) {
          onClose();
        }
      }
    });
  }

  getReferences(payload: ModelReferencePayload): Promise<unknown> {
    this.getReferenceMessages();
    if (payload.memberApp) {
      const apiConstants = { ...ApiConstants.getMemberAppReferences };
      apiConstants.url = apiConstants.url.replace('{appId}', payload.modelId);
      return new Promise((resolve, reject) => {
        this.http.get(apiConstants).subscribe({
          next: (res: unknown) => resolve(res),
          error: (err: unknown) => reject(err)
        });
      });
    }

    if (payload.targetMemberApp) {
      const apiConstants = { ...ApiConstants.getTargetMemberAppReferences };
      const suiteId = payload.appId || "";
      const appId = payload.modelId || "";
      apiConstants.url = apiConstants.url.replace('{appId}', appId).replace('{suiteId}', suiteId);
      return new Promise((resolve, reject) => {
        this.http.get(apiConstants).subscribe({
          next: (res: unknown) => resolve(res),
          error: (err: unknown) => reject(err)
        });
      });
    }
    if (payload.sharedDB) {
      const apiConstants = { ...ApiConstants.getSharedDBReferences };
      apiConstants.url = apiConstants.url.replace('{modelId}', payload.modelId);
      return new Promise((resolve, reject) => {
        this.http.get(apiConstants).subscribe({
          next: (res: unknown) => resolve(res),
          error: (err: unknown) => reject(err)
        });
      });
    }
    const { action, ...restPayload } = payload;
    const req: ModelReferencePayload = {
      ...restPayload,
      appId: payload.appId || this.appGlobalService.get('appId')
    } as ModelReferencePayload;
    return new Promise((resolve, reject) => {
      this.http.post(ApiConstants.getReferences, req).subscribe({
        next: (res: unknown) => resolve(res),
        error: (err: unknown) => reject(err)
      });
    });
  }

  /**
   * Retrieves references for multiple fields using the retrieve/all API.
   *
   * @param fieldArray - Array of field objects with modelId, modelType, actionType, appId
   * @returns Promise that resolves with the array of references
   */
  getMultipleFieldsReferences(fieldArray: any[]): Promise<unknown> {
    this.getReferenceMessages();
    return new Promise((resolve, reject) => {
      const apiConfig = ApiConstants.getModelReferencesAll;
      this.http[apiConfig.method](apiConfig, fieldArray).subscribe({
        next: (res: unknown) => resolve(res),
        error: (err: unknown) => reject(err)
      });
    });
  }

  openReferenceTree(
    node: { name: string; type: string },
    references: unknown[],
    action: string,
    extras?: Record<string, unknown>
  ): Promise<boolean> {
    this.getReferenceMessages();
    this.util.setPopupRestriction(true);
    const ref = this.dialog.open(ReferenceTreeViewComponent, {
      showHeader: false,
      styleClass: 'reference-tree',
      modal: true,
      data: {
        node,
        referenceList: references,
        action,
        ...(extras || {})
      }
    });
    return new Promise((resolve) => {
      ref.onClose.subscribe((data: { confirm?: boolean } | undefined) => {
        this.util.setPopupRestriction(false);
        const confirmed = !!data?.confirm;
        resolve(confirmed);
      });
    });
  }

  async handleWithReferences(params: {
    payload: ModelReferencePayload | ModelReferencePayload[];
    node?: { name: string; type: string };
    onConfirm: () => void;
    onNoReferences?: () => void;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
    filter?: (refs: unknown[]) => unknown[];
    extras?: Record<string, unknown>;
  }): Promise<void> {
    const { payload, node, onConfirm, onNoReferences, onCancel, onError, filter, extras } = params;
    try {
      let refsData = await this.fetchReferencesData(payload);
      refsData = this.applyFilterIfPresent(refsData, filter);
      await this.processReferencesResult({ refsData, payload, node, onConfirm, onNoReferences, onCancel, extras });
    } catch (err: unknown) {
      this.handleReferencesError(err, onError, onCancel, onNoReferences, onConfirm);
    }
  }

  private async fetchReferencesData(payload: ModelReferencePayload | ModelReferencePayload[]): Promise<unknown[]> {
    if (Array.isArray(payload)) {
      const cleanedPayload = payload.map(({ action: _action, ...rest }) => rest);
      return (await this.getMultipleFieldsReferences(cleanedPayload)) as unknown[];
    }
    return (await this.getReferences(payload)) as unknown[];
  }

  private applyFilterIfPresent(refsData: unknown[], filter?: (refs: unknown[]) => unknown[]): unknown[] {
    if (Array.isArray(refsData) && refsData.length > 0 && typeof filter === 'function') {
      return filter(refsData) || [];
    }
    return refsData;
  }

  private async processReferencesResult(params: {
    refsData: unknown[];
    payload: ModelReferencePayload | ModelReferencePayload[];
    node?: { name: string; type: string };
    onConfirm: () => void;
    onNoReferences?: () => void;
    onCancel?: () => void;
    extras?: Record<string, unknown>;
  }): Promise<void> {
    const { refsData, payload, node, onConfirm, onNoReferences, onCancel, extras } = params;
    const hasReferences = Array.isArray(refsData) && refsData.length > 0;
    if (!hasReferences) {
      (onNoReferences || onConfirm)();
      return;
    }
    if (!node) {
      onCancel?.();
      return;
    }
    const action = Array.isArray(payload) ? re.UiActions.DELETE : payload.action;
    const confirmed = await this.openReferenceTree(node, refsData, action, extras);
    if (confirmed) {
      onConfirm();
    } else {
      onCancel?.();
    }
  }

  private handleReferencesError(
    err: unknown,
    onError?: (err: unknown) => void,
    onCancel?: () => void,
    onNoReferences?: () => void,
    onConfirm?: () => void
  ): void {
    if (onError) {
      onError(err);
      return;
    }
    if (onCancel) {
      onCancel();
      return;
    }
    (onNoReferences || onConfirm)?.();
  }

  /**
   * Fetches all references for a given model ID using getReferences API.
   *
   * @param id - The model ID to fetch references for
   * @param apiName - The API constant key to use (defaults to 'getAllReferences')
   * @param idParamName - The parameter name for the ID (defaults to 'modelId')
   * @returns Promise that resolves with the references or rejects with an error
   */
  getAllReferences(
    id: string,
    apiName: string = 'getAllReferences',
    idParamName: 'modelId' | 'appId' = 'modelId'
  ): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.http.get(ApiConstants[apiName], { [idParamName]: id }).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: (err: any) => {
          reject(err);
        }
      });
    });
  }

  /**
   * Shows the "Where Used" functionality for an item.
   * Fetches references and either shows the reference tree or a "no references" dialog.
   *
   * @param config - Configuration for the where-used lookup
   * @param callbacks - Optional callbacks for loading state management
   * @returns Promise that resolves when the operation is complete
   */
  async showWhereUsed(
    config: {
      id: string;
      name: string;
      type: string;
      apiName?: string;
      idParamName?: 'modelId' | 'appId';
    },
    callbacks?: {
      hideLoader?: () => void;
    }
  ): Promise<void> {
    const apiName = config.apiName || 'getAllReferences';
    const idParamName = config.idParamName || 'modelId';

    try {
      const references = await this.getAllReferences(config.id, apiName, idParamName);

      callbacks?.hideLoader?.();

      if (references && references.length > 0) {
        this.openReferenceTreeDialog({
          node: { name: config.name, type: config.type },
          referenceList: references,
          action: 'WHERE_USED'
        });
      } else {
        this.util.showNoReferencesDialog(config.name, config.type);
      }
    } catch (error) {
      callbacks?.hideLoader?.();
      console.error('Error fetching references:', error);
      this.util.showError(this.translate.instant('ERROR_FETCHING_REFERENCES'));
    }
  }

  /**
   * Opens the reference tree dialog.
   *
   * @param config - Configuration for the reference tree dialog
   * @returns The dialog reference
   */
  openReferenceTreeDialog(config: {
    node: { name: string; type: string };
    referenceList: any[];
    action: string;
    rawData?: any;
  }) {
    const modalRef = this.dialog.open(ReferenceTreeViewComponent, {
      showHeader: false,
      styleClass: 'reference-tree',
      modal: true,
      data: {
        node: config.node,
        referenceList: config.referenceList,
        action: config.action
      }
    });

    return modalRef;
  }

  initializeMessages() {
    this.getConfirmationMessages().subscribe((res: any) => (this.confirmationMessageIds = res || {}));
  }

  getReferenceMessages(force = false) {
    if (
      force ||
      !this.messageBundleLoaded ||
      !this.confirmationMessageObj ||
      Object.keys(this.confirmationMessageObj).length === 0
    ) {
      const bundle = this.appGlobalService.get('referenceMessageBundle');
      if (bundle) {
        this.confirmationMessageObj = bundle;
        this.messageBundleLoaded = true;
      }
    }
  }

  // -------------------------form UsedIn Column---------------------------------------------------------

  getUsedInColumn(reference: any, metadata: any) {
    const dataReference = reference.data;
    const generatedPath: any = [];
    const pathData = this.generateOrderPath(dataReference, metadata);
    const pathOrder = pathData.path
      ?.split('/')
      .filter((elm) => elm !== null && elm !== undefined && elm !== '' && elm !== 'undefined');
    const pathtypeOrder = pathData.typePath
      ?.split('/')
      .filter((elm) => elm !== null && elm !== undefined && elm !== '' && elm !== 'undefined');
    pathOrder.map((p: string, index: number) => {
      generatedPath.push({
        key: this.getNameUsingMetaInfo(metadata, p) || re.PropertyType[p] || p,
        type: pathtypeOrder[index] ? this.updatePropName(pathtypeOrder[index]) : null
      });
    });
    return generatedPath;
  }

  camelToTitleCase(value: string): string {
    if (!value) return '';

    return value
      .toLowerCase()
      .replace(/[_\s-]+(.)?/g, (_, chr) => (chr ? chr.toUpperCase() : ''))
      .replace(/^./, (str) => str.toUpperCase());
  }

  updatePropName(name: string) {
    const updatedName = name === 'INDEX_DEFINITION' ? 'Index' : name;
    return this.snakeToTitleCase(updatedName);
  }

  generateOrderPath(reference: any, md: any) {
    let path = '';
    let typePath = '';
    const type = reference.componentType;
    const ai = reference?.additionalInfo || {};
    switch (type) {
      case re.ComponentType.dataModel:
        if (reference.propertyName == 'QUERY_BUILDER' && reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION') {
          path = `${ai.currentTableId}/${reference.propertyName}/${this.camelToTitleCase(ai.actionSequence)}/${ai.tableAliasId}/${this.camelToTitleCase(ai.action)}/${reference.referredModelId}`;
          typePath = `Table/Property/ActionSequence/AliasTable/Action/Query Name`;
        } else if (ai.path && reference.referredModelType === 'QUERY_BUILDER') {
          path = `${ai.path}/${reference.propertyName}/${reference.referredModelId}`;
          typePath = `Table/Field/Property/Query Name`;
        } else if (reference.propertyName === 'FORMULA_BUILDER') {
          path = `${ai.path}/${reference.propertyName}`;
          typePath = `Table/Field/Property`;
        } else if (reference.propertyName === 'TRANSACTION_TABLE' || reference.propertyName === 'PARENT_TABLE') {
          path = `${ai.tableId}/${reference.modelId}/${reference.propertyName}`;
          typePath = `Table/Property`;
        } else if (reference.modelType === 'LOOKUP_TEMPLATE') {
          path = `${ai.tableId}/${reference.modelId}/${reference.propertyName}`;
          typePath = `Lookup/Property`;
        } else if (
          reference.propertyName === 'DATA_POPULATION_STRATEGY_FIELD' ||
          reference.propertyName === 'LOOKUP_OUTPUT_MAPPING' ||
          reference.propertyName === 'LOOKUP_DISPLAY_FIELD' ||
          reference.propertyName === 'DATA_POPULATION_STRATEGY_FOREIGN_KEY'
        ) {
          path = `${ai.tableId}/${ai.fieldId}/${reference.propertyName}`;
          typePath = `Table/Field/Property`;
        } else if (
          reference.propertyName === 'RUNTIME_SHARED_FIELDS' ||
          reference.propertyName === 'RUNTIME_VISIBILITY_OVERRIDE' ||
          reference.propertyName === 'RUNTIME_VALUE_OVERRIDE' ||
          reference.propertyName === 'RUNTIME_STATUS_FIELD'
        ) {
          path = `${reference.modelId}/${reference.propertyName}`;
          typePath = `Table/Property`;
        } else if (reference.propertyName === 'BQ_DATASET_VALUE') {
          path = `${ai.tableId || reference.modelId}/Dataset Name`;
          typePath = `Table/Property`;
        } else if (reference.propertyName === 'CUSTOM_ACTION_PERMISSION') {
          const actName = this.translate.instant(reference.modelId);
          path = `${ai.tableId}/Table Actions and Permissions/${actName}`;
          typePath = `Table/Property/${reference.modelId ? reference.modelType : ''}`;
        } else if (
          reference.propertyName === 'LOOKUPTO_FILTER_INPUT_MAPPING_FIELDS' ||
          reference.propertyName === 'LOOKUPTO_OUTPUT_MAPPING_FIELDS'
        ) {
          path = `${ai.path}/${reference.propertyName}`;
          typePath = `Table/field/property`;
        } else if (reference.propertyName === 'SEARCHABLE') {
          path = `${reference?.additionalInfo?.tableId}/${reference?.propertyName}`;
          typePath = `Table/property`;
        } else if (reference.propertyName === 'QUERY_OPTIONAL_FILTERS') {
          path = `${reference.modelId}/Optional Input Filters`;
          typePath = `Table/Property`;
        } else if (
          reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION' &&
          reference.propertyName === 'CUD_QUERY_VIEW_FIELD_MAPPING' &&
          reference.referredModelType === 'FIELD'
        ) {
          path = `${ai.queryViewTableId}/QueryView Action Configuration - Map Fields`;
          typePath = `Table/Property`;
        } else if (
          reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION' &&
          reference.propertyName === 'CUD_FIELD_MAPPING' &&
          reference.referredModelType === 'FIELD'
        ) {
          path = `${ai.queryViewTableId}/QueryView Action Configuration - Map Fields`;
          typePath = `Table/Property`;
        } else if (
          reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION' &&
          reference.propertyName === 'CUD_TABLE_ALIAS' &&
          reference.referredModelType === 'TABLE'
        ) {
          path = `${ai.tableId}/QueryView Action Configuration - Select Table`;
          typePath = `Table/Property`;
        } else if (
          reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION' &&
          reference.propertyName === 'CUD_TABLE_MAPPING' &&
          reference.referredModelType === 'TABLE'
        ) {
          path = `${ai.queryViewTableId}/QueryView Action Configuration - Map Fields/${ai.queryViewFieldId}`;
          typePath = `Table/Property/Field Mapping`;
        } else if (
          reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION' &&
          reference.propertyName === 'RECORD_IDENTIFIER_INDEX'
        ) {
          path = `${ai.queryViewTableId}/QueryView Action Configuration - Record Identifier Key/${ai.aliasSid}`;
          typePath = `Table/Property/Record Identifier Key`;
        } else if (reference.modelType === 'QUERYVIEW_CUD_CONFIGURATION') {
          path = `${ai.queryViewTableId}/${reference.propertyName}`;
          typePath = `Table/Property`;
        } else if (reference.propertyName === 'PARENT_TABLE_FIELD') {
          path = `${reference.referredModelId}/${reference.modelId}`;
          typePath = `Table/${reference.modelId ? reference.modelType : ''}`;
        } else if (
          reference.propertyName === 'TABLE_INDEX' &&
          reference.modelType === 'INDEX_DEFINITION' &&
          reference.referredModelType === 'TABLE'
        ) {
          path = `${reference.referredModelId}/${reference.modelId}`;
          typePath = `Table/Index`;
        } else if (
          reference.propertyName === 'PARENT_TABLE_FOREIGN_KEY' &&
          reference.modelType === 'FOREIGN_KEY' &&
          reference.referredModelType === 'TABLE'
        ) {
          path = `${reference.referredModelId}/${reference.modelId}`;
          typePath = `Table/Foreign Key`;
        } else if (
          reference.propertyName === 'QUERY_FILTERS' &&
          reference.modelType === 'TABLE' &&
          reference.referredModelType === 'FIELD'
        ) {
          path = `${reference.modelId}/Fields For Unique Record For Lookup`;
          typePath = `Table/Property`;
        } else if (
          reference.propertyName === 'QUERY_MANDATORY_FILTERS' &&
          reference.modelType === 'TABLE' &&
          reference.referredModelType === 'FIELD'
        ) {
          path = `${reference.modelId}/Mandatory Input Parameters`;
          typePath = `Table/Property`;
        } else if (reference.propertyName === 'QUERY_STRING') {
          path = `${reference.modelId}/Query`;
          typePath = `Table/Query`;
        } else {
          path = `${ai.tableId}/${reference.modelId}`;
          typePath = `Table/${reference.modelId ? reference.modelType : ''}`;
        }
        break;
      case re.ComponentType.workflow: {
        path = `${ai.path}` || `${reference.modelId}`;
        if (reference.propertyName === 'WORKFLOW_ACTOR_STEP' || reference.propertyName === 'WORKFLOW_ACTION_STEP') {
          path = `${path}${reference.modelId}`;
        }
        const temp = this.getWorkflowActors(path, md);
        path = temp.join('/');
        typePath = `${temp[0] ? 'Workflow' : ''}/${temp[1] ? 'Status' : ''}/${temp[2] ? 'Actor' : ''}/${temp[3] ? 'Action' : ''}`;
        if (reference.propertyName !== 'WORKFLOW_ACTOR_STEP' && reference.propertyName !== 'WORKFLOW_ACTION_STEP') {
          path = `${path}/${reference.propertyName}`;
          typePath = `${typePath}/Property`;
        }
        if (reference.propertyName === 'WORKFLOW_TABLE') {
          path = `${reference.modelId}/${reference.referredModelId}`;
          typePath = `Workflow/Table`;
        }
        break;
      }
      case re.ComponentType.service: {
        path = `${reference.modelId}/${ai.path ? ai.path : ''}`;
        let temp1 = path?.split('/') || [];
        const val = this.getNameUsingMetaInfo(md, temp1[temp1.length - 1]);
        const propertryNotNeeded1 = this.compareStrings(val, reference?.propertyName);
        if (!propertryNotNeeded1) {
          path = `${path}/${reference.propertyName}`;
          temp1 = path?.split('/');
        }
        typePath = `${temp1[0] ? 'Service' : ''}/${temp1[1] ? 'Node' : ''}/${temp1[2] && !propertryNotNeeded1 ? 'Property' : ''}`;

        break;
      }
      case re.ComponentType.pages: {
        const o = this.getPagePath(reference, ai, md);
        path = o.path;
        typePath = o.typePath;

        break;
      }
      case re.ComponentType.customComponent:
        path = `${reference.propertyName}`;
        typePath = `Property`;
        break;
      case re.ComponentType.queryBuilder:
        if (reference.propertyName === 'DEFAULT_FILTER_PAGE_VARIABLE') {
          path = `${reference?.additionalInfo?.pageId}/Default Filtering Criteria/Page Variables`;
          reference.componentType = 'Pages';
          typePath = `Page/property`;
        } else {
          path = `${ai.path ? ai.path : reference.propertyName}`;
          typePath = ``;
        }
        break;
      case re.ComponentType.userStories:
        path = `${reference.modelId}/${reference.propertyName}`;
        typePath = `User Story/Property`;
        break;
      case re.ComponentType.roles:
        if (reference.referredModelType === 'CLIENT') {
          path = `${reference.modelId}`;
          typePath = `Role`;
        }
        break;
      case re.ComponentType.database:
        path = `${reference.appId}/${reference.propertyName}`;
        typePath = `App/Property`;
        break;
      case re.ComponentType.apis:
        if (
          reference.referredModelType === 'ACL_CUSTOM_ACTION' ||
          reference.referredModelType === 'TABLE' ||
          reference.referredModelType === 'INDEX' ||
          reference.referredModelType === 'SERVICE'
        ) {
          path = `${ai.clientId}/${ai.funcCompId}/${reference.modelId}`;
          typePath = `REST - Exposed Client/Functional Component/API`;
        }
        if (reference.referredModelType === 'EXPOSURE') {
          path = `${ai.appId}/${ai.providerId}/${ai.apiGroupId}/${reference.modelId}`;
          typePath = `App/REST - Consumed Provider/Functional Component/API`;
        }
        if (reference.referredModelType === 'ENVIRONMENT' && reference.modelType === 'PROVIDER') {
          path = `${reference.modelId}/${reference.propertyName}`;
          typePath = `Provider/Property`;
        }
        break;
      case re.ComponentType.queryString:
        path = `${reference.modelId}/Query`;
        typePath = `Query String/Query`;
        break;
      case re.ComponentType.appSettings:
        if (reference.referredModelType === 'ENVIRONMENT' && reference.modelType === 'APPLICATION_CONFIGURATION') {
          path = `${reference.modelId}/${reference.propertyName}`;
          typePath = `Configuration Key/Property`;
        } else {
          path = `${reference.propertyName}`;
          typePath = `Property`;
        }
        break;
      default:
        path = `${reference.propertyName}`;
        typePath = `Property`;
    }

    const appId = this.appGlobalService.get('appId');
    if (appId !== reference.appId && !typePath.startsWith('App/')) {
      path = `${reference.appId}/${path}`;
      typePath = `App/${typePath}`;
    }
    return {
      path: path,
      typePath: typePath
    };
  }

  snakeToTitleCase(snakeCase: string) {
    const sentence = snakeCase.toLowerCase();
    return sentence
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getPagePath(reference, ai, md) {
    let path = `${reference.modelId}/${ai.path ? ai.path : ''}`;
    let pagesTemp = path?.split('/') || [];
    // const lastValue = this.getNameUsingMetaInfo(md,pagesTemp[(pagesTemp.length -1)]);
    const values: any[] = [];
    let propertryNotNeeded = false;
    pagesTemp.map((k: any) => {
      values.push(this.getNameUsingMetaInfo(md, k));
    });
    values.map((o: any) => {
      if (this.compareStrings(o, reference?.propertyName)) {
        propertryNotNeeded = true;
      }
    });
    // const propertryNotNeeded = this.compareStrings(lastValue,reference?.propertyName);
    const tempPath: any = [];
    pagesTemp.map((o: any, index: number) => {
      let d = '';
      if (index === 0) {
        d = 'Page';
      } else {
        d = this.pageProperties[this.getNameUsingMetaInfo(md, o)];
      }

      tempPath.push(d || 'Node');
    });
    let typePath = tempPath.join('/');
    if (!propertryNotNeeded) {
      path = `${path}/${reference.propertyName}`;
      pagesTemp = path?.split('/');
    }
    typePath = `${typePath}/${!propertryNotNeeded ? 'Property' : ''}`;

    // Map of property names to their column path values (string or function that returns string based on ai)
    const columnPathMap: Record<string, string | ((ai: any) => string)> = {
      PAGES_OUTPUT_MAPPING_SOURCE_FIELD: (ai) =>
        ai.foreignKeyId != null ? 'Referenced Table Fields' : ai.queryViewId != null ? 'Query View Fields' : '',
      PAGES_INPUT_MAPPING_SOURCE_FIELD: (ai) =>
        ai.foreignKeyId != null ? 'Referenced Fields' : ai.queryViewId != null ? 'Query View Parameters' : '',
      PARAMETER_MAPPING_SOURCE_FIELD: 'Fields in List Page',
      PARAMETER_MAPPING_TARGET_FIELD: 'Fields or Variables in Detail Page',
      STANDARD_GRID_PARAMETER_MAPPING_PAGE_VARIABLES_SOURCE_FIELD: 'Page Variables Defined in List Page',
      STANDARD_GRID_PARAMETER_MAPPING_PAGE_VARIABLES_TARGET_FIELD: 'Fields or Variables in Detail Page',
      QUERY_VIEW_MAPPING_SOURCE_FIELD: 'List Page Query Parameters',
      QUERY_VIEW_MAPPING_TARGET_FIELD: 'Map from Fields or Variables',
      QUERY_VIEW_MAPPING_PAGE_VARIABLES_SOURCE_FIELD: 'Page Variables',
      QUERY_VIEW_MAPPING_PAGE_VARIABLES_TARGET_FIELD: 'Fields or Variables in Detail Page',
      PAGES_INPUT_MAPPING_TARGET_FIELD: 'Map from Fields or Variables',
      PAGES_OUTPUT_MAPPING_TARGET_FIELD: 'Map to Fields',
      DETAIL_PAGE_PARAMETERS_TARGET_FIELD: 'Fields or Variables in Detail Page',
      DETAIL_PAGE_PARAMETERS_SOURCE_FIELD: 'Fields or Variables in List Page',
      PARAMETER_MAPPING_QUERY_VIEW_TARGET_FIELD: 'Mapping',
      PARAMETER_MAPPING_QUERY_VIEW_SOURCE_FIELD: 'Mandatory Parameters',
      DETAIL_PAGE_MAPPING_SOURCE_FIELD: 'Detail Page Query Parameters',
      DETAIL_PAGE_MAPPING_TARGET_FIELD: 'Fields or Variables in List Page',
      NAVIGATION_DATA_MAPPING_SOURCE_FIELD: 'Variables From List Page',
      NAVIGATION_DATA_MAPPING_TARGET_FIELD: 'Variables or Fields To Detail Page',
      HOLD_VALUE_PARAMS_MAPPING_SOURCE_FIELD: 'Fields or Variables in List Page',
      HOLD_VALUE_PARAMS_MAPPING_TARGET_FIELD: 'Fields or Variables in Detail Page',
      DETAIL_VIEW_PARAMS_MAPPING_TARGET_FIELD: 'Fields or Variables in List Page'
    };

    // Check if property name exists in the map and add column path
    const columnConfig = columnPathMap[reference.propertyName];
    if (columnConfig) {
      const columnPath = typeof columnConfig === 'function' ? columnConfig(ai) : columnConfig;
      if (columnPath) {
        path = `${path}/${columnPath}`;
        typePath = `${typePath}/Column`;
      }
    }

    return {
      path: path,
      typePath: typePath
    };
  }

  // -------------------------form Confirmation Messages---------------------------------------------------------

  getMessageBasedonInfo(action: string, referenceData: any, metaData: any, data?: any): string {
    if (
      referenceData?.componentType === GlobalConstants.dataModel &&
      this.appGlobalService.get('transientFieldUpdate') === 'true'
    ) {
      action = 'TRANSIENT_UPDATE';
    }
    const dataobj = data?.node;
    const fieldProps = data?.fieldProps;
    const item = {
      name: dataobj?.name || '',
      table: dataobj?.f_tableId ? this.getNameUsingMetaInfo(metaData, dataobj?.f_tableId) : '',
      tableId: dataobj?.f_tableId
    };

    const type = this.getReferredType(referenceData, dataobj);
    const referenceAction = this.getReferenceAction(referenceData, fieldProps, action, data, type);
    const msg =
      (this.confirmationMessageIds[type] &&
        this.confirmationMessageIds[type][referenceData.propertyName] &&
        this.confirmationMessageIds[type][referenceData.propertyName][referenceAction]) ||
      '';
    return this.enrichMessage(msg, referenceData, metaData, data);
  }

  getReferenceAction(referenceData: any, dataobj: any, action: string, data: any, modelType: string) {
    let type: string = action;
    if (
      ['File Attachments', 'Image Carousel', 'Lookup'].includes(dataobj?.changedFieldType) &&
      referenceData.propertyName === 'FPK'
    ) {
      type = 'DEFAULT_UPDATE';
    } else if (['File Attachments', 'Image Carousel'].includes(dataobj?.changedFieldType)) {
      switch (referenceData.propertyName) {
        case 'LOOKUP_FIELDS':
        case 'LOOKUP_OUTPUT_MAPPING_FIELDS':
        case 'LOOKUPTO_FILTER_INPUT_MAPPING_FIELDS':
        case 'IMPORT_FIELDS':
        case 'EXPORT_FIELDS':
        case 'EXPORT_FILTERS':
        case 'INDEX':
        case 'MANDATORY_CONDITIONAL':
          type = 'DEFAULT_UPDATE';
          break;
        default:
      }
    } else if (data.propertyAction === 'ALLOWED_VALUES_RENAME') {
      type = 'RENAME';
    }
    if (data?.typeHasImpact && modelType === 'TABLE') {
      switch (referenceData.propertyName) {
        case 'PARENT_TABLE':
        case 'FOREIGN_KEY_FIELDS':
        case 'FOREIGN_KEY_PARENT_INDEX':
        case 'WORKFLOW_TABLE':
        case 'LOOKUP_TO':
        case 'TABLE_PAGE':
          type = 'UPDATE';
          break;
        default:
      }
    } else if (!data?.typeHasImpact && modelType === 'TABLE') {
      switch (referenceData.propertyName) {
        case 'PARENT_TABLE':
        case 'FOREIGN_KEY_FIELDS':
        case 'FOREIGN_KEY_PARENT_INDEX':
        case 'WORKFLOW_TABLE':
        case 'LOOKUP_TO':
          type = 'DEFAULT_UPDATE';
          break;
        default:
      }
    }
    return type;
  }

  getReferredType(referenceData: any, dataobj: any) {
    const itemType = (dataobj?.type || '').toUpperCase();
    if (referenceData.parentReferredModelType === 'ROLE' && referenceData.referredModelType === 'FIELD') {
      return 'FIELD';
    } else if (referenceData.parentReferredModelType === 'TABLE' && referenceData.referredModelType === 'PAGE') {
      return 'PAGE';
    }

    const qbtype = itemType === 'ROLE' ? itemType : 'FIELD';
    if (referenceData.parentReferredModelType) {
      return referenceData.parentReferredModelType;
    } else if (referenceData.referredModelType === 'QUERY_BUILDER') {
      return `QUERY_BUILDER_${qbtype}`;
    }
    return referenceData.referredModelType;
  }

  getWarningMessages(ref: any, genAction: string, metaData: any, item: any): any {
    let dataWarning = '';
    let codeWarning = '';
    const type = ref.referredModelType === re.ModelType.PAGE ? ref.referredModelType : item?.node?.type?.toUpperCase();
    const action = item.propertyAction ? item.propertyAction : genAction;
    if (type === re.ModelType.LOOKUP || type === re.ModelType.FIELD || type === re.ModelType.ROLE) {
      // if (action===re.UiActions.DELETE || action===re.UiActions.UPDATE) {
      codeWarning = this.confirmationMessageIds.customWarning?.[type]?.[action];
      dataWarning = this.confirmationMessageIds.DataWarning?.[type]?.[action];
      // }
    } else if (type === re.ModelType.TABLE || type === re.ModelType.PAGE || type === re.ModelType.WORKFLOW) {
      codeWarning = this.confirmationMessageIds.customWarning[type]?.[action];
      dataWarning = this.confirmationMessageIds.DataWarning[type]?.[action];
    }
    return {
      dataWarning: this.enrichMessage(dataWarning, ref, metaData, item.node),
      codeWarning: this.enrichMessage(codeWarning, ref, metaData, item.node)
    };
  }

  enrichMessage(msgId = '', referenceData: any, metadata: any, data?: any, additional?: any) {
    const dataobj = data?.node;
    const referenceMetaData: any = {};
    const item = {
      name: metadata[data?.additionalProps?.name] || dataobj?.name || '',
      table: dataobj?.f_tableId ? this.getNameUsingMetaInfo(metadata, dataobj?.f_tableId) : '',
      tableId: dataobj?.f_tableId
    };
    this.getReferredItemsList(referenceData, metadata, referenceMetaData);
    let msg = this.confirmationMessageObj[msgId] || msgId;

    if ((msgId === 'REF_0321' || msgId === 'REF_0322') && !referenceData?.additionalInfo?.aliasSid) {
      msg = msg.replace("('{aliasSid}')", '');
    }

    // Replace referredModel with alias name if foreignKeySid is present in case of referring parent table fields in elastic search
    if (referenceData?.additionalInfo?.foreignKeySid) {
      const aliasNameId = `${referenceData.additionalInfo.foreignKeySid}_$_${referenceData.referredModelId}`;
      msg = msg.replace('{referredModel}', `'{referredModel}' alias '${metadata[aliasNameId]}'`);
    }
    const replacements = this.formReplacementsObj(referenceMetaData, item, referenceData);
    const result = msg.replace(
      new RegExp(Object.keys(replacements).join('|'), 'g'),
      (matched) => replacements[matched]
    );
    return result;
  }

  getReferredItemsList(keyList: any, metaData: any, referenceMetaData, compDetails?: any) {
    for (const [key, value] of Object.entries(keyList)) {
      if (typeof value === 'string' && key !== 'path') {
        referenceMetaData[key] = this.getNameUsingMetaInfo(metaData, value) || null;
      } else if (key === 'additionalInfo') {
        this.getReferredItemsList(keyList.additionalInfo, metaData, referenceMetaData, keyList);
      } else if (key === 'path') {
        const obj = this.formAdditionalInfo(keyList.path, compDetails, referenceMetaData);
        this.getReferredItemsList(obj, metaData, referenceMetaData);
      }
    }
    this.formatRawIds(metaData, referenceMetaData);
  }

  formAdditionalInfo(path: string, compDetails: any, mdata: any) {
    const obj: any = {};
    const d = path?.split('/');
    switch (compDetails.componentType) {
      case re.ComponentType.workflow:
        obj.workflow = d[0] || compDetails.modelId;
        obj.workflowActor = d[2] || '';
        break;
      case re.ComponentType.dataModel: // For QueryBuilder(taking table and field details from path)
        obj.tableId = d[0] || '';
        obj.fieldId = d[1] || '';
        break;
      case re.ComponentType.pages: // to get the button name for queryBuilders
        if (d.length > 3) {
          obj.buttonName = d[3];
        } else if (
          compDetails.propertyName === 'NAVIGATION_PAGE' &&
          mdata[compDetails.model] === 'App Navigation' &&
          d.length > 2
        ) {
          obj.menuName = d[2] || '';
        }
        break;
      default:
    }
    return obj;
  }

  formReplacementsObj(d: any, item?: any, referenceData?: any) {
    // Use referredModelId (resolved name) first - handles both single and multiple fields
    return {
      '{referredModel}': d['referredModelId'],
      '{template}': d['templateId'],
      '{model}': d['modelId'] || d['tableAliasId'],
      '{table}': d['tableId'] || item?.table,
      '{field}': d['fieldId'],
      '{step}': d['step'] || d['status'],
      '{actor}': d['actor'],
      '{action}': d['action'],
      '{workflow}': d['workflow'],
      '{workflowActor}': d['workflowActor'] || d['actor'],
      '{item}': d['referredModelId'] || item?.name || '',
      '{itemTable}': item.table,
      '{actionName}': d['modelId'] ? this.translate.instant(d['modelId']) : '',
      '{buttonName}': d['buttonName'],
      '{role}': d['roleSid'],
      '{menu}': d['menu'] || d['menuName'],
      '{property}': re.PropertyType[referenceData['propertyName']],
      '{foreignkeyName}': d['foreignKey'],
      '{technicalRole}': d['modelId'],
      '{appName}': d['appId'],
      '{provider}': d['providerId'],
      '{apigroup}': d['apiGroupdId'],
      '{aliasSid}': d['aliasSid'],
      '{tableId}': d['tableId'],
      '{actionNameCud}': this.camelToTitleCase(referenceData?.additionalInfo?.action),
      '{actionSequence}': this.camelToTitleCase(referenceData?.additionalInfo?.actionSequence),
      '{tableAliasId}': d['tableAliasId'],
      '{tableIdCud}': d['currentTableId']
    };
  }
  getConfirmationMessages(): Observable<any> {
    const subject = new Observable<any>((observer) => {
      observer.next(referenceConfirmation as any);
    });
    return subject;
  }

  getNameUsingMetaInfo(metaInfo: any, id: string) {
    return id && metaInfo[id] ? this.translate.instant(metaInfo[id]) : '';
  }

  compareStrings(a: string, b: string) {
    const data1 = a?.replace(/[_\s]/g, '')?.toLowerCase() || '';
    const data2 = b?.replace(/[_\s]/g, '')?.toLowerCase() || '';
    return data1 === data2;
  }

  getWorkflowActors(path: string, md: any) {
    const temp = path.split('/');
    const actors = this.getNameUsingMetaInfo(md, temp[2])?.split('_$_') || [];
    const d: any = [];
    actors?.map((o: string) => {
      d.push(this.getNameUsingMetaInfo(md, o) || null);
    });
    temp[2] = d.join(',');
    return temp;
  }

  formatRawIds(metaData: any, referenceMetaData: any = {}) {
    for (const key in referenceMetaData) {
      if (typeof referenceMetaData[key] === 'string' && referenceMetaData[key]?.indexOf('_$_') > -1) {
        const t: any = [];
        const d: any = referenceMetaData[key].split('_$_');
        d?.map((o: string) => {
          t.push(this.getNameUsingMetaInfo(metaData, o) || null);
        });
        referenceMetaData[key] = t.join(',');
      } else if (
        typeof referenceMetaData[key] === 'string' &&
        this.getNameUsingMetaInfo(metaData, referenceMetaData[key])
      ) {
        referenceMetaData[key] = this.getNameUsingMetaInfo(metaData, referenceMetaData[key]);
      }
    }
  }

  /**
   * Request table refresh in querystring-list component
   */
  requestRefresh(data: any) {
    this.refreshSubject.next({ data: data });
  }

  /**
   * Request table refresh with specific data
   */
  requestRefreshWithData(data: any) {
    this.refreshSubject.next({ action: 'update', data });
  }
}
